import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { Props as FormattedMessageProps } from 'react-intl/src/components/message';

import { Link } from '@mui/material';

import { ROUTES } from '@arrived/common';
import { CONFIG } from '@arrived/config';

interface CommonQuestion {
  title: FormattedMessageProps;
  description: FormattedMessageProps;
  link?: string;
}

export enum CommonQuestionsKeys {
  HOW_ARRIVED_WORKS,
  EXPECTED_RETURNS,
  SEEKING_TENANT,
  DIVERSIFICATION,
  INVESTING_HORIZON,
  PREPARING_VACATION_RENTAL,
}

export const LTR_COMMON_QUESTIONS = [
  CommonQuestionsKeys.HOW_ARRIVED_WORKS,
  CommonQuestionsKeys.EXPECTED_RETURNS,
  CommonQuestionsKeys.SEEKING_TENANT,
  CommonQuestionsKeys.DIVERSIFICATION,
  CommonQuestionsKeys.INVESTING_HORIZON,
];

export const STR_COMMON_QUESTIONS = [
  CommonQuestionsKeys.HOW_ARRIVED_WORKS,
  CommonQuestionsKeys.EXPECTED_RETURNS,
  CommonQuestionsKeys.PREPARING_VACATION_RENTAL,
  CommonQuestionsKeys.DIVERSIFICATION,
  CommonQuestionsKeys.INVESTING_HORIZON,
];

export const COMMON_QUESTIONS: Record<CommonQuestionsKeys, CommonQuestion> = {
  [CommonQuestionsKeys.HOW_ARRIVED_WORKS]: {
    title: { id: 'how-arrived-works', defaultMessage: 'How Arrived Works' },
    description: {
      id: 'how-arrived-works-description',
      defaultMessage: `Arrived acquires rental properties into an LLC and sells shares in that LLC to the general public.
      Arrived then manages the day to day operations including finding tenants and completing repairs.Investors receive
      cash dividends from rental income each quarter and capture any property value appreciation.`,
    },
  },
  [CommonQuestionsKeys.EXPECTED_RETURNS]: {
    title: { id: 'expected-returns', defaultMessage: 'What Returns Can I Expect' },
    description: {
      id: 'expected-returns-description',
      defaultMessage: `Investing in Arrived rental homes can deliver returns to investors in two different ways: 1) property value growth & 2) rental income.
      While we cannot predict future returns, below are historic returns that can be helpful.
      {br}
      {br}
      <strong>1) Property value growth:</strong> Any property value appreciation can result in increased investment value for the investor.
      Over the last 20 years, single family homes in the USA have appreciated an average of 4.0% per year. That means that for a property with
       a 65% mortgage loan, the home equity value increased an average of 4.9% per year. For a home without a mortgage loan, the home equity value increase
       would average 2.3% per year. These returns include transaction & disposition fees and assumes the investment is held for 10 years.
       {br}
       {br}
      <strong>2) Rental income:</strong> Historically, Arrived properties have paid cash dividends from rental income that translate to 2.4% - 7.9%
       annual returns on investment. The rental income you receive will be proportional to your ownership in the property.
       {br}
       {br}
      <strong>In summary</strong> adding both the property value growth & the rental income, we see that <strong>investing in rental homes has
       historically resulted in returns on investment between 4.7% - 12.8% per year.</strong> It is important to note that past performance
       may not be indicative of future results.`,
      values: {
        br: <br />,
        strong: (parts: any) => <strong>{parts}</strong>,
      },
    },
    link: `${CONFIG.helpUrl}articles/4953922-what-returns-can-i-expect-from-arrived-properties`,
  },
  [CommonQuestionsKeys.SEEKING_TENANT]: {
    title: {
      id: 'still-seeking-tenant',
      defaultMessage: 'What to expect if my property is still seeking a tenant',
    },
    description: {
      id: 'still-seeking-tenant-description',
      defaultMessage: `We are currently focused on marketing the rental homes to prospective tenants and will email you
      when a new lease has been signed. Arrived's strategy for seeking tenants is focused on signing 2 year leases,
      achieving market rent, and thoroughly vetting applicants. Though it may take a bit more time to lease out the
      homes, we believe these standards provide our investors the best way to maximize returns over the long term.`,
    },
    link: `${CONFIG.helpUrl}articles/5935265-what-to-expect-if-my-property-is-still-seeking-a-tenant`,
  },
  [CommonQuestionsKeys.DIVERSIFICATION]: {
    title: { id: 'diversification', defaultMessage: 'Diversification' },
    description: {
      id: 'diversification-description',
      defaultMessage: `With real estate, it can be beneficial to invest in multiple properties and markets to achieve
      portfolio diversification. Diversifying your portfolio can be a good way to reduce exposure to risk from an
      individual property, tenant, or market forces.`,
    },
  },
  [CommonQuestionsKeys.INVESTING_HORIZON]: {
    title: { id: 'investing-horizon', defaultMessage: 'Investing Horizon & Liquidity' },
    description: {
      id: 'investing-horizon-description',
      defaultMessage: `Currently investors will need to plan to hold their shares for the full investment period until the
      property is sold and investors are paid their proportional proceeds from the sale. In the future we intend to offer
      an option to access liquidity during the investment period, but there can be no guarantee when that will be available,
      and there may be fees involved.{br}{br}
      <strong>Arrived has filed a planned redemption program with the U.S. Securities & Exchange Commission (SEC), and it is
      currently under review for Qualification.</strong> A redemption program would allow investors the ability to sell their
      shares back to Arrived and receive the financial proceeds from the sale. Disclaimer: Arrived will not be able to
      support early redemption requests until the program is reviewed and qualified. This review is still pending, and we
      will be updating this FAQ as soon as it is complete. Additionally, there may be fees associated with any early
      redemption requests. For risk factors and disclaimer information, you can review our
      {communicationsDisclaimerLink}.{br}{br}
      Arrived strives to give investors the opportunity to build wealth through real estate. Historically, real estate
      returns have been maximized when treated as a long-term investment over multiple years. Arrived property offerings
      typically have a 5 year minimum investment period before a property would be sold.`,
      values: {
        communicationsDisclaimerLink: (
          <Link href={ROUTES.communicationsDisclaimer} target="_blank">
            <FormattedMessage
              id="common-questions-communications-disclaimer-link-text"
              defaultMessage="communications disclaimer"
            />
          </Link>
        ),
        strong: (parts: ReactNode) => <strong>{parts}</strong>,
        br: <br />,
      },
    },
  },
  [CommonQuestionsKeys.PREPARING_VACATION_RENTAL]: {
    title: {
      id: 'preparing-vacation-rental-title',
      defaultMessage: 'What is the process to prepare a vacation rental for bookings?',
    },
    description: {
      id: 'preparing-vacation-rental-description',
      defaultMessage: `Preparing a newly-funded vacation rental for bookings can take an average of 1-3 months
      depending on a number of factors for each property. We work with experienced design & furnishing partners that
      have a strong track record of standing up and operating top performing vacation rentals quickly and efficiently.
      Together with these partners we develop a property theme, set a budget and timeline, and work with local
      contractors, furniture suppliers, and our property managers to ready not only the property but also all the
      additional services (cleaning, customer support, pricing optimization…etc).`,
    },
    link: `${CONFIG.helpUrl}articles/6531820-what-is-the-process-to-prepare-a-vacation-rental-for-bookings`,
  },
};
